<template>
    <div class="main-layout__container">
        <div class="content__header">
            <div class="content-header__title">Пользователи</div>

            <div class="content-header__actions">
                <router-link :to="{ name: 'AddUser' }">
                    <UISimpleButton>
                        <template #icon>
                            <img src="@/assets/icons/ui/plus-white.svg" />
                        </template>
                        Добавить
                    </UISimpleButton>
                </router-link>
            </div>

            <div class="content-header__background content-header__background--1-line-filters"></div>
        </div>

        <FilterPanel
            :columns="4"
            @search="onSearch"
            @reset="onReset"
        >
            <UIInput
                id="email"
                v-model="usersFilter.email"
                type="text"
                label="Email"
            />

            <UISelect
                id="dealer_id"
                v-model="usersFilter.dealer_id"
                :options="dealerOptions"
                label="Дилер"
                multiple
                searchable
            />

            <UISelect
                id="is_admin"
                v-model="usersFilter.is_admin"
                :options="roleOptions"
                label="Роль"
            />

            <UISelect
                id="status"
                v-model="usersFilter.status"
                label="Статус"
                :options="statusOptions"
                multiple
            />
        </FilterPanel>

        <UISearchResults
            :count="usersPaginationMeta?.pagination.total || 0"
            :is-visible-sort="false"
        />

        <UILoader v-if="isUsersLoading" class="tableable" />

        <div v-else class="content__body">
            <DataTable
                v-if="arUsers?.length"
                :headers="headers"
                :data="arUsers"
                :sort-key="sortKey"
                :sort-order="sortOrder"
                has-actions
                @sortable="changeSort"
                @row-click="openUserModal"
            >
                <template #is_admin="{ row }">
                    {{ row.is_admin ? 'Администратор' : 'Пользователь' }}
                </template>

                <template #status="{ row }">
                    {{ getUserStatusLabel(row.status) }}
                </template>

                <template #actions="{ row }">
                    <UIDropDown as-modal-on-mobile>
                        <template #elements>
                            <router-link
                                :to="{ name: 'EditUser', params: { user_id: row.id } }"
                                class="ui-dropdown-menu__element"
                            >
                                <img v-svg-inline src="@/assets/icons/ui/edit.svg" />
                                <span>Редактировать</span>
                            </router-link>

                            <li
                                v-if="row.status === UserStatus.Deactivated"
                                class="ui-dropdown-menu__element"
                                @click="handleActivateUser(row as User)"
                            >
                                <img v-svg-inline src="@/assets/icons/ui/check.svg" />
                                <span>Активировать</span>
                            </li>

                            <li
                                v-if="row.status === UserStatus.Active"
                                class="ui-dropdown-menu__element danger"
                                @click="handleDeactivateUser(row as User)"
                            >
                                <img v-svg-inline src="@/assets/icons/ui/mingcute_close-circle-line.svg" />
                                <span>Деактивировать</span>
                            </li>

                            <li
                                v-if="canApproveUser(row as User)"
                                class="ui-dropdown-menu__element"
                                @click="handleApproveUser(row as User)"
                            >
                                <img v-svg-inline src="@/assets/icons/ui/check.svg" />
                                <span>Подтвердить</span>
                            </li>

                            <li
                                v-if="canRejectUser(row as User)"
                                class="ui-dropdown-menu__element danger"
                                @click="handleRejectUser(row as User)"
                            >
                                <img v-svg-inline src="@/assets/icons/ui/mingcute_close-circle-line.svg" />
                                <span>Отклонить</span>
                            </li>
                        </template>
                    </UIDropDown>
                </template>
            </DataTable>

            <UINotFoundData v-else />
        </div>

        <div class="content__footer">
            <TablePagination
                :total-pages="usersPaginationMeta?.pagination.last_page ?? 1"
                :current-page="usersPaginationMeta?.pagination.current_page ?? 1"
                :items-per-page="usersPaginationMeta?.pagination.per_page ?? 10"
                @page-change="changePage"
            />
        </div>
    </div>
</template>

<script lang="ts" setup>
    import { ref, onMounted, inject, h, computed } from 'vue';
    import { useRoute } from 'vue-router';

    import { ModalPlugin } from '@/plugins/modalPlugin';

    import { SelectOption } from '@/types/form.type.ts';
    import { DealerListItem } from '@/types/dealer.type.ts';
    import {
        getUserStatusLabel,
        getUserStatusSelectOptions,
        User,
        UsersPaginatorItem,
        UsersRequestFilter,
        UserStatus,
    } from '@/types/user.type.ts';

    import useDealerRepository from '@/repositories/dealer/useDealerRepository.ts';
    import useNotifications from '@/utils/useNotifications.ts';
    import useUserRepository from '@/repositories/user/useUserRepository.ts';
    import usePermission from '@/repositories/auth/usePermission.ts';
    import { useUsersFilterStore } from '@/stores/users/users-filter.ts';

    import DataTable from '@/components/shared/DataTable/DataTable.vue';
    import FilterPanel from '@/components/shared/FilterPanel/FilterPanel.vue';
    import TablePagination from '@/components/shared/TablePagination/TablePagination.vue';

    import UIDropDown from '@/components/ui/UIDropDown/UIDropDown.vue';
    import UIInput from '@/components/ui/UIInput/UIInput.vue';
    import UILoader from '@/components/ui/UILoader/UILoader.vue';
    import UINotFoundData from '@/components/ui/UINotFoundData/UINotFoundData.vue';
    import UISearchResults from '@/components/ui/UISearchResults/UISearchResults.vue';
    import UISelect from '@/components/ui/UISelect/UISelect.vue';
    import UISimpleButton from '@/components/ui/UISimpleButton/UISimpleButton.vue';

    import UserModal from '@/modals/UserModal/UserModal.vue';

    import ConfirmActionModal from '@/components/modals/ConfirmActionModal/ConfirmActionModal.vue';

    import UserConfirmActivate from '@/components/users/UserConfirmActivate/UserConfirmActivate.vue';
    import UserConfirmApprove from '@/components/users/UserConfirmApprove/UserConfirmApprove.vue';
    import UserConfirmDeactivate from '@/components/users/UserConfirmDeactivate/UserConfirmDeactivate.vue';
    import UserConfirmReject from '@/components/users/UserConfirmReject/UserConfirmReject.vue';

    const $modal = inject('$modal') as typeof ModalPlugin;

    const route = useRoute();

    const {
        arUsers,
        usersPaginationMeta,
        isUsersLoading,
        getUsers,
        activateUser,
        deactivateUser,
        approveUser,
        rejectUser,
    } = useUserRepository();
    const { getAllActiveDealers } = useDealerRepository();
    const { canApproveUser, canRejectUser } = usePermission();
    const usersFilterStore = useUsersFilterStore();
    const { showSuccess } = useNotifications();

    const usersFilter = ref<UsersRequestFilter>({ ...usersFilterStore.$state });
    const dealerOptions = ref<SelectOption[]>([]);
    const statusOptions = ref<SelectOption[]>(getUserStatusSelectOptions());
    const roleOptions = ref<SelectOption[]>([
        { label: 'Администратор', value: 1 },
        { label: 'Пользователь', value: 0 },
    ]);
    const headers = ref([
        { label: 'ФИО', key: 'full_name' },
        { label: 'Название ДЦ', key: 'dealer_name', sortable: true },
        { label: 'Email', key: 'email' },
        { label: 'Роль', key: 'is_admin' },
        { label: 'Статус', key: 'status' },
    ]);

    const sortKey = computed<string | undefined>(() => {
        if (usersFilter.value.sort) {
            return Object.keys(usersFilter.value.sort[0] ?? {})[0];
        }

        return undefined;
    });

    const sortOrder = computed<string | undefined>(() => {
        if (usersFilter.value.sort) {
            return Object.values(usersFilter.value.sort[0] ?? {})[0];
        }

        return undefined;
    });

    function changePage(page: number): void {
        usersFilter.value.page = page;
        fetchUsers();
    }

    function changeSort(key: string, dir: 'asc' | 'desc' | undefined): void {
        usersFilter.value.sort = dir === undefined ? [{ dealer_name: 'asc' }] : [{ [key]: dir }];
        fetchUsers();
    }

    function onSearch(): void {
        usersFilter.value.page = 1;
        fetchUsers();
    }

    function onReset(): void {
        usersFilter.value = {
            dealer_id: undefined,
            email: '',
            status: undefined,
            is_admin: undefined,
            page: 1,
            sort: usersFilter.value.sort,
        };

        fetchUsers();
    }

    function openUserModal(user: UsersPaginatorItem): void {
        $modal.open({
            component: UserModal,
            props: {
                userId: user.id,
            },
        });
    }

    function handleActivateUser(user: User): void {
        $modal.open({
            component: ConfirmActionModal,
            modalType: 'narrow',
            slots: {
                default: () =>
                    h(UserConfirmActivate, { user }),
            },
            props: {
                onConfirm: async () => {
                    await activateUser(user.id);

                    showSuccess({
                        title: 'Пользователь активирован',
                    });

                    fetchUsers();
                },
            },
        });
    }

    function handleDeactivateUser(user: User): void {
        $modal.open({
            component: ConfirmActionModal,
            modalType: 'narrow',
            slots: {
                default: () =>
                    h(UserConfirmDeactivate, { user }),
            },
            props: {
                onConfirm: async () => {
                    await deactivateUser(user.id);

                    showSuccess({
                        title: 'Пользователь деактивирован',
                    });

                    fetchUsers();
                },
            },
        });
    }

    function handleApproveUser(user: User): void {
        $modal.open({
            component: ConfirmActionModal,
            modalType: 'narrow',
            slots: {
                default: () =>
                    h(UserConfirmApprove, { user }),
            },
            props: {
                onConfirm: async () => {
                    await approveUser(user.id);

                    showSuccess({
                        title: 'Пользователь подтвержден',
                    });

                    fetchUsers();
                },
            },
        });
    }

    function handleRejectUser(user: User): void {
        $modal.open({
            component: ConfirmActionModal,
            modalType: 'narrow',
            slots: {
                default: () =>
                    h(UserConfirmReject, { user }),
            },
            props: {
                onConfirm: async () => {
                    await rejectUser(user.id);

                    showSuccess({
                        title: 'Пользователь отклонен',
                    });

                    fetchUsers();
                },
            },
        });
    }

    function mapUsersFilterToRequestFilter(): UsersRequestFilter
    {
        const requestFilter: UsersRequestFilter = {
            page: usersFilter.value.page,
            sort: usersFilter.value.sort,
        };

        if (usersFilter.value.dealer_id !== undefined && usersFilter.value.dealer_id.length > 0) {
            requestFilter.dealer_id = usersFilter.value.dealer_id;
        }

        if (usersFilter.value.email !== undefined && usersFilter.value.email.length > 0) {
            requestFilter.email = usersFilter.value.email;
        }

        if (usersFilter.value.status !== undefined && usersFilter.value.status.length > 0) {
            requestFilter.status = usersFilter.value.status;
        }

        if (usersFilter.value.is_admin !== undefined) {
            requestFilter.is_admin = usersFilter.value.is_admin;
        }

        return requestFilter;
    }

    function fetchUsers(): void {
        usersFilterStore.$patch({ ...usersFilter.value });
        getUsers(mapUsersFilterToRequestFilter());
    }

    function fetchFilterOptions(): void {
        getAllActiveDealers().then((dealers: DealerListItem[]) => {
            const options: SelectOption[] = [];
            dealers.forEach((dealer: DealerListItem) => {
                options.push({
                    label: dealer.name + ' (' + dealer.sap_code + ')',
                    value: dealer.id,
                });
            });

            dealerOptions.value = options;
        });
    }

    function applyRouteRequestParams(): void {
        applyRouteRequestStatusParam();
    }

    function applyRouteRequestStatusParam(): void {
        if (!route.query.status) {
            return;
        }

        if (!Object.values(UserStatus).includes(route.query.status)) {
            return;
        }

        usersFilter.value.status = [route.query.status as UserStatus];
    }

    onMounted(() => {
        applyRouteRequestParams();
        fetchUsers();
        fetchFilterOptions();
    });
</script>
